import { useEffect, useMemo, useState } from "react";
import { Pencil, Trash2, GripVertical, TrendingUp, TrendingDown } from "lucide-react";
import { query } from "../../services/biApi";
import { formatar, delta, MENOR_MELHOR, NEUTRA } from "./formatos";
import { LineWidget, BarWidget, DonutWidget } from "./WidgetCharts";
import CriativoModal from "./CriativoModal";

// Um widget = uma config + uma consulta na camada semântica. O container cuida de
// carregar, erro, cabeçalho e os controles do modo edição; o corpo varia por tipo.

function montarBody(widget, filtrosGlobais) {
    const base = {
        start: filtrosGlobais.start, end: filtrosGlobais.end,
        filtros: {
            ...(filtrosGlobais.produto_id ? { produto_id: filtrosGlobais.produto_id } : {}),
            ...(filtrosGlobais.conta_id ? { conta_id: filtrosGlobais.conta_id } : {}),
            ...(filtrosGlobais.campanha_id ? { campanha_id: filtrosGlobais.campanha_id } : {}),
            ...(widget.filtros || {}),
        },
        metrics: widget.metrics,
    };
    const op = widget.opcoes || {};
    switch (widget.tipo) {
        case "kpi": return { ...base, dimensions: [], compare: !!filtrosGlobais.compare && op.compare !== false };
        case "funnel": return { ...base, dimensions: [], compare: false };
        case "line": return { ...base, dimensions: [widget.dimension || "dia"], compare: !!filtrosGlobais.compare && (op.compare ?? false) && (widget.dimension || "dia") === "dia", limit: 400 };
        case "bar": return { ...base, dimensions: [widget.dimension || "dia"], limit: op.limite || (widget.dimension === "dia" ? 400 : 12), order: widget.dimension === "dia" ? { by: "dia", dir: "asc" } : { by: op.ordenar || widget.metrics[0], dir: "desc" } };
        case "donut": return { ...base, dimensions: [widget.dimension || "origem"], limit: op.limite || 8, order: { by: widget.metrics[0], dir: "desc" } };
        case "table": return { ...base, dimensions: [widget.dimension || "campanha"], limit: op.limite || 50, order: { by: op.ordenar || widget.metrics[0], dir: "desc" } };
        case "creatives": return { ...base, dimensions: ["anuncio"], limit: op.limite || 12, order: { by: op.ordenar || widget.metrics[0], dir: "desc" } };
        default: return base;
    }
}

export default function Widget({ widget, filtrosGlobais, catalogo, editando, onEditar, onRemover }) {
    const [estado, setEstado] = useState({ loading: true, erro: null, data: null });
    const body = useMemo(() => montarBody(widget, filtrosGlobais), [widget, filtrosGlobais]);
    const chave = JSON.stringify(body);

    useEffect(() => {
        if (widget.tipo === "texto") { setEstado({ loading: false, erro: null, data: null }); return; }
        let vivo = true;
        setEstado(s => ({ ...s, loading: true, erro: null }));
        query(body).then(d => vivo && setEstado({ loading: false, erro: null, data: d }))
            .catch(e => vivo && setEstado({ loading: false, erro: e.response?.data?.message || e.message, data: null }));
        return () => { vivo = false; };
    }, [chave]); // eslint-disable-line react-hooks/exhaustive-deps

    const metaDe = (k) => catalogo?.metricas?.find(m => m.key === k) || { key: k, label: k, formato: "inteiro" };
    const dimLabel = (k) => catalogo?.dimensoes?.find(d => d.key === k)?.label || k;

    return (
        <div className={`surface-card h-full flex flex-col overflow-hidden ${editando ? "ring-1 ring-brand-500/30" : ""}`}>
            <div className="flex items-center gap-2 px-4 pt-3 pb-1 shrink-0">
                {editando && <span className="drag-handle cursor-move text-ink-4 hover:text-ink-2"><GripVertical className="h-4 w-4" /></span>}
                <h3 className="text-xs font-semibold text-ink-2 truncate flex-1" title={widget.titulo}>{widget.titulo || metaDe(widget.metrics?.[0]).label}</h3>
                {estado.loading && <span className="h-3 w-3 border border-brand-400 border-t-transparent rounded-full animate-spin" />}
                {editando && (
                    <div className="flex gap-1">
                        <button onClick={() => onEditar(widget)} className="text-ink-4 hover:text-ink-1 p-1" title="Editar"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => onRemover(widget)} className="text-ink-4 hover:text-danger-400 p-1" title="Remover"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                )}
            </div>
            <div className="flex-1 min-h-0 px-4 pb-3">
                {estado.erro && <p className="text-xs text-danger-400">{estado.erro}</p>}
                {!estado.erro && widget.tipo === "texto" && <p className="text-sm text-ink-2 whitespace-pre-wrap">{widget.opcoes?.texto}</p>}
                {!estado.erro && estado.data && (
                    widget.tipo === "kpi" ? <Kpi data={estado.data} metric={metaDe(widget.metrics[0])} /> :
                    widget.tipo === "funnel" ? <Funnel data={estado.data} metrics={widget.metrics.map(metaDe)} /> :
                    widget.tipo === "table" ? <Tabela data={estado.data} metrics={widget.metrics.map(metaDe)} dim={widget.dimension || "campanha"} dimLabel={dimLabel(widget.dimension || "campanha")} /> :
                    widget.tipo === "creatives" ? <Criativos data={estado.data} metrics={widget.metrics.map(metaDe)} /> :
                    widget.tipo === "line" ? <LineWidget data={estado.data} metrics={widget.metrics.map(metaDe)} dim={widget.dimension || "dia"} /> :
                    widget.tipo === "bar" ? <BarWidget data={estado.data} metrics={widget.metrics.map(metaDe)} dim={widget.dimension || "dia"} horizontal={!!widget.opcoes?.horizontal} empilhado={!!widget.opcoes?.empilhado} /> :
                    widget.tipo === "donut" ? <DonutWidget data={estado.data} metric={metaDe(widget.metrics[0])} dim={widget.dimension || "origem"} /> : null
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
function Kpi({ data, metric }) {
    const v = data.totais?.[metric.key];
    const ant = data.comparativo?.totais?.[metric.key];
    const d = data.comparativo ? delta(v, ant) : null;
    const bom = d === null ? null : NEUTRA.has(metric.key) ? null : (MENOR_MELHOR.has(metric.key) ? d < 0 : d > 0);
    return (
        <div className="h-full flex flex-col justify-center">
            <div className="font-display text-3xl font-bold tabular-nums text-ink-1 truncate" title={formatar(v, metric.formato)}>{formatar(v, metric.formato)}</div>
            {d !== null && (
                <div className={`mt-1 flex items-center gap-1 text-xs ${bom === null ? "text-ink-3" : bom ? "text-positive-400" : "text-danger-400"}`}>
                    {d === Infinity ? <TrendingUp className="h-3.5 w-3.5" /> : d > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : d < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : null}
                    <span>{d === Infinity ? "novo" : `${d > 0 ? "+" : ""}${d.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`}</span>
                    <span className="text-ink-5">vs {formatar(ant, metric.formato)}</span>
                </div>
            )}
            {d === null && <p className="text-[11px] text-ink-5 mt-1">{data.periodo?.start === data.periodo?.end ? data.periodo?.start?.split("-").reverse().join("/") : `${data.periodo?.start?.split("-").reverse().slice(0, 2).join("/")} → ${data.periodo?.end?.split("-").reverse().slice(0, 2).join("/")}`}</p>}
        </div>
    );
}

function Funnel({ data, metrics }) {
    const vals = metrics.map(m => ({ ...m, v: Number(data.totais?.[m.key] || 0) }));
    const max = Math.max(1, ...vals.map(x => x.v));
    return (
        <div className="h-full flex flex-col justify-center gap-1.5 overflow-y-auto">
            {vals.map((m, i) => {
                const ant = i > 0 ? vals[i - 1].v : null;
                const pct = ant ? (m.v / ant) * 100 : null;
                const w = Math.max(3, (m.v / max) * 100);
                return (
                    <div key={m.key} className="grid grid-cols-[150px_1fr_90px] items-center gap-2">
                        <span className="text-[11px] text-ink-3 truncate" title={m.label}>{m.label}</span>
                        <div className="h-5 bg-surface-3 rounded overflow-hidden">
                            <div className="h-full rounded bg-gradient-to-r from-brand-500 to-violet-500" style={{ width: `${w}%` }} />
                        </div>
                        <div className="text-right">
                            <span className="text-xs font-semibold tabular-nums text-ink-1">{formatar(m.v, "inteiro")}</span>
                            {pct !== null && <span className="block text-[10px] text-ink-5">{pct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%</span>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function Tabela({ data, metrics, dim, dimLabel }) {
    const [ordem, setOrdem] = useState(null);
    const [criativo, setCriativo] = useState(null);
    const rows = useMemo(() => {
        const r = [...(data.rows || [])];
        if (ordem) r.sort((a, b) => { const x = a[ordem.by] ?? -Infinity, y = b[ordem.by] ?? -Infinity; return ordem.dir === "asc" ? (x > y ? 1 : -1) : (x < y ? 1 : -1); });
        return r;
    }, [data, ordem]);
    const tot = data.totais || {};
    function clicar(k) { setOrdem(o => o && o.by === k ? { by: k, dir: o.dir === "desc" ? "asc" : "desc" } : { by: k, dir: "desc" }); }
    return (
        <div className="h-full overflow-auto">
            <table className="w-full text-xs">
                <thead className="sticky top-0 bg-surface-2">
                    <tr className="text-ink-3 border-b border-white/[0.08]">
                        <th className="text-left py-1.5 pr-2 font-medium">{dimLabel}</th>
                        {metrics.map(m => <th key={m.key} onClick={() => clicar(m.key)} className={`text-right py-1.5 px-2 font-medium cursor-pointer whitespace-nowrap hover:text-ink-1 ${ordem?.by === m.key ? "text-brand-300" : ""}`} title={m.nota || m.label}>{m.label}{ordem?.by === m.key ? (ordem.dir === "desc" ? " ↓" : " ↑") : ""}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className="border-b border-white/[0.04] hover:bg-surface-3/40">
                            <td className="py-1.5 pr-2 max-w-[320px] text-ink-2" title={r[`${dim}_label`] || r[dim]}>
                                <div className="flex items-center gap-2 min-w-0">
                                    {r.anuncio_meta?.thumb && <img src={r.anuncio_meta.thumb} alt="" className="h-7 w-7 rounded object-cover shrink-0 bg-surface-3" loading="lazy" />}
                                    <span className="truncate">{r[`${dim}_label`] || r[dim] || <span className="text-ink-5">(sem {dimLabel.toLowerCase()})</span>}</span>
                                    {r.anuncio_meta && dim === "anuncio" && <button onClick={() => setCriativo({ id: r.anuncio, nome: r.anuncio_label })} className="text-brand-300 hover:text-brand-200 text-[10px] underline shrink-0">ver</button>}
                                </div>
                            </td>
                            {metrics.map(m => <td key={m.key} className="text-right py-1.5 px-2 tabular-nums whitespace-nowrap">{formatar(r[m.key], m.formato)}</td>)}
                        </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={metrics.length + 1} className="text-center text-ink-5 py-6">Sem dados no período</td></tr>}
                </tbody>
                {rows.length > 1 && (
                    <tfoot className="sticky bottom-0 bg-surface-2">
                        <tr className="border-t border-white/[0.1] font-semibold">
                            <td className="py-1.5 pr-2 text-ink-2">Total</td>
                            {metrics.map(m => <td key={m.key} className="text-right py-1.5 px-2 tabular-nums">{formatar(tot[m.key], m.formato)}</td>)}
                        </tr>
                    </tfoot>
                )}
            </table>
            {data.truncado && <p className="text-[10px] text-ink-5 mt-1">Lista cortada. Aumente o limite no widget.</p>}
            {criativo && <CriativoModal adId={criativo.id} nome={criativo.nome} onClose={() => setCriativo(null)} />}
        </div>
    );
}

// Grade de criativos: miniatura + nome + métricas, "Ver" abre preview/mídia/download.
function Criativos({ data, metrics }) {
    const [criativo, setCriativo] = useState(null);
    const rows = (data.rows || []).filter(r => r.anuncio && !String(r.anuncio).startsWith("nome:"));
    if (!rows.length) return <p className="text-xs text-ink-4 py-6 text-center">Nenhum anúncio da Meta no período (ou a conta ainda não sincronizou).</p>;
    return (
        <div className="h-full overflow-y-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                {rows.map(r => {
                    const m = r.anuncio_meta || {};
                    return (
                        <div key={r.anuncio} className="bg-surface-2 border border-white/[0.06] rounded-xl overflow-hidden flex flex-col hover:border-brand-500/40 transition">
                            <button onClick={() => setCriativo({ id: r.anuncio, nome: r.anuncio_label })} className="relative aspect-square bg-surface-3 overflow-hidden">
                                {m.thumb ? <img src={m.thumb} alt="" className="h-full w-full object-cover" loading="lazy" /> : <div className="h-full w-full grid place-items-center text-ink-5 text-xs">sem miniatura</div>}
                                {m.status && <span className={`absolute top-1.5 left-1.5 text-[9px] px-1.5 py-0.5 rounded-full ${m.status === "ACTIVE" ? "bg-positive-500/80 text-white" : "bg-black/60 text-ink-2"}`}>{m.status === "ACTIVE" ? "ativo" : m.status.toLowerCase().replace(/_/g, " ")}</span>}
                            </button>
                            <div className="p-2.5 flex-1 flex flex-col gap-1">
                                <p className="text-[11px] font-medium text-ink-1 truncate" title={r.anuncio_label}>{r.anuncio_label}</p>
                                <p className="text-[10px] text-ink-5 truncate" title={m.conjunto}>{m.conjunto || m.campanha || ""}</p>
                                <div className="mt-auto grid grid-cols-2 gap-x-2 gap-y-0.5">
                                    {metrics.slice(0, 4).map(mt => (
                                        <div key={mt.key} className="min-w-0"><p className="text-[9px] text-ink-5 truncate">{mt.label}</p><p className="text-[11px] tabular-nums text-ink-1 truncate">{formatar(r[mt.key], mt.formato)}</p></div>
                                    ))}
                                </div>
                                <button onClick={() => setCriativo({ id: r.anuncio, nome: r.anuncio_label })} className="mt-1.5 text-[11px] text-brand-300 hover:text-brand-200 underline text-left">Ver criativo</button>
                            </div>
                        </div>
                    );
                })}
            </div>
            {criativo && <CriativoModal adId={criativo.id} nome={criativo.nome} onClose={() => setCriativo(null)} />}
        </div>
    );
}
